import React from "react";
import { motion } from "framer-motion";
import { MapPin, Star, ArrowUpRight, ShieldCheck, Trees } from "lucide-react";
import PreReservationDialog from "./PreReservationDialog";

const TERRAINS = [
  {
    id: "fougeres",
    siteValue: "fougeres",
    badge: "Second site",
    title: "Paintball Fougères",
    location: "20 Boulevard Nelson Mandela, 35300 Fougères",
    description:
      "Découvrez notre nouvelle localisation ! 3 terrains de jeux aménagés et entièrement sécurisés par des filets de protection hauts de plusieurs mètres.",
    highlights: [
      "3 terrains aménagés",
      "Filets de protection pro",
      "Accès facile Fougères centre",
    ],
    img: "https://customer-assets.emergentagent.com/job_terrain-paintball/artifacts/ne273enr_Foug%C3%A8res%20Visuel.png",
    alt: "Terrain paintball Fougères — matériel et masques Paintball Aventures 35 activité groupe Fougères",
    accent: "#E91E63",
  },
  {
    id: "louvigne",
    siteValue: "louvigne-du-desert",
    badge: "Site historique",
    title: "Paintball Louvigné-du-Désert",
    location: "La Morinais, 35420 Louvigné-du-Désert",
    description:
      "Plus de 20 ans d'expérience ! Notre site historique vous accueille au cœur d'un magnifique cadre en forêt avec 3 terrains de jeux immersifs.",
    highlights: [
      "3 terrains immersifs en forêt",
      "+20 ans d'expérience",
      "Cadre naturel exceptionnel",
    ],
    img: "https://customer-assets.emergentagent.com/job_terrain-paintball/artifacts/uwb6zll6_Louvign%C3%A9%20Visuel.png",
    alt: "Terrain paintball Louvigné-du-Désert — lanceurs paintball alignés site historique forêt",
    accent: "#00E676",
  },
];

export default function Terrains() {
  return (
    <section
      id="terrains"
      data-testid="terrains-section"
      className="relative bg-white py-20 md:py-28 lg:py-32"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12 md:mb-16">
          <div>
            <p className="font-mono text-xs font-bold uppercase tracking-[0.3em] text-[#E91E63] mb-3">
              02 — Nos terrains
            </p>
            <h2 className="font-display text-4xl sm:text-5xl md:text-6xl font-bold uppercase leading-[0.95] tracking-tight">
              2 terrains de paintball
              <br />
              en <span className="text-[#E91E63]">Ille-et-Vilaine</span>
            </h2>
          </div>
          <p className="text-base md:text-lg text-gray-700 max-w-md">
            Deux sites distincts, une même passion. Choisissez votre arène et réservez votre session pour groupes, EVG, EVJF ou comités d&apos;entreprise.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-6 lg:gap-10">
          {TERRAINS.map((t, i) => (
            <motion.article
              key={t.id}
              id={t.id}
              data-testid={`terrain-card-${t.id}`}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              className="group relative border-2 border-black bg-white overflow-hidden"
            >
              <div className="relative aspect-[4/3] overflow-hidden bg-black">
                <img
                  src={t.img}
                  alt={t.alt}
                  className="absolute inset-0 w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/0 to-transparent pointer-events-none" />
                <div
                  className="absolute top-4 right-4 px-3 py-1.5 font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-black"
                  style={{ background: t.accent }}
                >
                  {t.badge}
                </div>
                <div className="absolute bottom-4 left-4 right-4 text-white">
                  <p className="font-display text-2xl md:text-3xl lg:text-4xl font-bold uppercase leading-none drop-shadow-[0_2px_4px_rgba(0,0,0,0.6)]">
                    {t.title}
                  </p>
                </div>
              </div>

              <div className="p-6 md:p-8">
                <div className="flex items-start gap-2 text-sm font-bold text-gray-900 mb-4">
                  <MapPin className="w-4 h-4 mt-0.5 text-[#E91E63]" strokeWidth={2.5} />
                  <address className="not-italic">{t.location}</address>
                </div>

                <p className="text-base text-gray-700 leading-relaxed mb-6">
                  {t.description}
                </p>

                <ul className="space-y-2 mb-6">
                  {t.highlights.map((h) => (
                    <li
                      key={h}
                      className="flex items-center gap-2 text-sm text-gray-800"
                    >
                      <Star
                        className="w-4 h-4 text-[#E91E63] shrink-0"
                        strokeWidth={2.5}
                        fill="#E91E63"
                      />
                      {h}
                    </li>
                  ))}
                </ul>

                <PreReservationDialog
                  defaultSite={t.siteValue}
                  trigger={
                    <button
                      data-testid={`terrain-${t.id}-reserve`}
                      className="inline-flex items-center gap-2 font-bold uppercase text-sm tracking-wider text-black hover:text-[#E91E63] transition-colors"
                    >
                      Réserver sur ce site
                      <ArrowUpRight className="w-4 h-4" strokeWidth={2.5} />
                    </button>
                  }
                />
              </div>
            </motion.article>
          ))}
        </div>

        {/* Strip with assurance items */}
        <div className="mt-12 md:mt-16 grid sm:grid-cols-3 gap-px bg-gray-200 border-2 border-black">
          {[
            { icon: ShieldCheck, t: "Sécurité maximale", s: "Équipement homologué + briefing complet" },
            { icon: Trees, t: "Pleine nature", s: "Terrains immersifs en forêt bretonne" },
            { icon: Star, t: "Note 5 ★ Google", s: "Plus de 20 ans à votre service" },
          ].map((item) => (
            <div
              key={item.t}
              className="bg-white p-6 md:p-8 flex items-start gap-4"
            >
              <item.icon className="w-7 h-7 text-[#E91E63] shrink-0" strokeWidth={2.5} />
              <div>
                <p className="font-display font-bold uppercase text-base md:text-lg">
                  {item.t}
                </p>
                <p className="text-sm text-gray-600 mt-1">{item.s}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
